import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import shrinkImage from '../../utils/shrinkImage';
import { useSettings } from '../../context/SettingsContext';

const CONDITIONS = ['excellent','very good','good','fair','poor'];
const STATUS_OPTS = ['available','sold','reserved','deleted'];

function ProductForm({ product, categories, onSave, onCancel }) {
  const isNew = !product?.id;
  const [form, setForm] = useState(product || { title:'', category_id:'', subcategory_id:'', price:'', original_price:'', condition:'good', quantity:1, is_firearm:0, make:'', model:'', caliber:'', serial_number:'', description:'', status:'available', featured:0, bravo_sku:'', list_on_website:1, list_on_ebay:1, shipping_price:'', pickup_only:0 });
  const { settings } = useSettings();
  const [images, setImages] = useState(product?.images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const subs = categories.find(c => c.id === parseInt(form.category_id))?.subcategories || [];

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const data = new FormData();
    data.append('image', await shrinkImage(file));
    try {
      const r = await api.post('/upload/image', data, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      if (product?.id) {
        await api.post(`/products/${product.id}/images`, { url: r.data.url, public_id: r.data.public_id, is_primary: images.length === 0 ? 1 : 0 });
      }
      setImages(prev => [...prev, { url: r.data.url, public_id: r.data.public_id, is_primary: prev.length === 0 ? 1 : 0 }]);
    } catch (err) { setError(`Image upload failed: ${err.response?.data?.error || err.message}`); }
    setUploading(false);
  };

  const removeImage = async (img, i) => {
    if (product?.id && img.id) {
      await api.delete(`/products/${product.id}/images/${img.id}`).catch(() => {});
    }
    setImages(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    if (!form.title || !form.price) return setError('Title and price are required');
    setSaving(true); setError('');
    try {
      if (isNew) {
        const r = await api.post('/products', form);
        // Upload any staged images
        for (let i = 0; i < images.length; i++) {
          await api.post(`/products/${r.data.id}/images`, { url: images[i].url, public_id: images[i].public_id, is_primary: i === 0 ? 1 : 0 });
        }
      } else {
        await api.put(`/products/${product.id}`, form);
      }
      onSave();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1a2744' }}>{isNew ? 'Add New Item' : 'Edit Item'}</h2>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9a9a9a' }}>✕</button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="grid-2" style={{ gap: 20, marginBottom: 16 }}>
        <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Title *</label><input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Glock 19 Gen 5 9mm" /></div>
        <div className="form-group"><label className="form-label">Category</label>
          <select className="form-control" value={form.category_id} onChange={e => { set('category_id', e.target.value); set('subcategory_id', ''); }}>
            <option value="">Select...</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Subcategory</label>
          <select className="form-control" value={form.subcategory_id} onChange={e => set('subcategory_id', e.target.value)} disabled={!subs.length}>
            <option value="">Select...</option>
            {subs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Price *</label><input className="form-control" type="number" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} placeholder="0.00" /></div>
        <div className="form-group"><label className="form-label">Original Price</label><input className="form-control" type="number" step="0.01" value={form.original_price} onChange={e => set('original_price', e.target.value)} placeholder="0.00 (optional)" /></div>
        <div className="form-group"><label className="form-label">Condition</label>
          <select className="form-control" value={form.condition} onChange={e => set('condition', e.target.value)}>
            {CONDITIONS.map(c => <option key={c} value={c} style={{ textTransform: 'capitalize' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Quantity</label><input className="form-control" type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} min="0" /></div>
        {!isNew && <div className="form-group"><label className="form-label">Status</label>
          <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>}
        <div className="form-group"><label className="form-label">Bravo SKU</label><input className="form-control" value={form.bravo_sku} onChange={e => set('bravo_sku', e.target.value)} placeholder="From Bravo POS" /></div>
      </div>

      {/* Sales channels */}
      <div style={{ marginBottom: 16 }}>
        <label className="form-label" style={{ display: 'block', marginBottom: 8 }}>Where should this item be sold?</label>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', background: '#f8f7f2', borderRadius: 10, padding: '12px 16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            <input type="checkbox" checked={!!form.list_on_website} onChange={e => set('list_on_website', e.target.checked ? 1 : 0)} />
            🌐 Website
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            <input type="checkbox" checked={!!form.list_on_ebay} onChange={e => set('list_on_ebay', e.target.checked ? 1 : 0)} disabled={!!form.is_firearm} />
            🛒 eBay
          </label>
          {!form.list_on_website && !form.list_on_ebay && (
            <span style={{ fontSize: 12, color: '#9a9a9a', fontStyle: 'italic' }}>Bravo inventory only — not for sale online</span>
          )}
        </div>
      </div>

      {/* Shipping */}
      <div style={{ marginBottom: 16 }}>
        <label className="form-label" style={{ display: 'block', marginBottom: 8 }}>Shipping</label>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', background: '#f8f7f2', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['Standard', ''], ['Heavy', parseFloat(settings.heavy_shipping_price || 15).toFixed(2)]].map(([label, val]) => {
              const on = !form.pickup_only && String(form.shipping_price ?? '') === val;
              return (
                <button key={label} type="button" className={`btn btn-sm ${on ? 'btn-navy' : 'btn-outline'}`}
                  onClick={() => { set('pickup_only', 0); set('shipping_price', val); }}>
                  {label} ${val || parseFloat(settings.default_shipping_price || 0).toFixed(2)}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: form.pickup_only ? 0.4 : 1 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>$</span>
            <input className="form-control" type="number" step="0.01" min="0" style={{ width: 120 }} disabled={!!form.pickup_only}
              value={form.shipping_price ?? ''} onChange={e => set('shipping_price', e.target.value)}
              placeholder={`${parseFloat(settings.default_shipping_price || 0).toFixed(2)} default`} />
            <span style={{ fontSize: 12, color: '#9a9a9a' }}>or a custom price · blank = Standard</span>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            <input type="checkbox" checked={!!form.pickup_only} onChange={e => set('pickup_only', e.target.checked ? 1 : 0)} />
            🏪 In-store pickup only (too big or fragile to ship)
          </label>
        </div>
      </div>

      {/* Firearm toggle */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'center', background: '#f8f7f2', borderRadius: 10, padding: '12px 16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
          <input type="checkbox" checked={!!form.is_firearm} onChange={e => { set('is_firearm', e.target.checked ? 1 : 0); set('requires_ffl', e.target.checked ? 1 : 0); if (e.target.checked) set('list_on_ebay', 0); }} />
          🔒 This is a firearm (FFL transfer required)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
          <input type="checkbox" checked={!!form.featured} onChange={e => set('featured', e.target.checked ? 1 : 0)} />
          ⭐ Featured item
        </label>
      </div>

      {!!form.is_firearm && (
        <p style={{ fontSize: 12, color: '#9a9a9a', marginTop: -8, marginBottom: 16 }}>
          Firearms are never auto-listed to eBay — eBay's marketplace policy prohibits it. This item will only appear on our own website.
        </p>
      )}

      {form.is_firearm ? (
        <div className="grid-2" style={{ gap: 16, marginBottom: 16 }}>
          <div className="form-group"><label className="form-label">Make</label><input className="form-control" value={form.make} onChange={e => set('make', e.target.value)} placeholder="Glock, Smith & Wesson..." /></div>
          <div className="form-group"><label className="form-label">Model</label><input className="form-control" value={form.model} onChange={e => set('model', e.target.value)} placeholder="19, M&P Shield..." /></div>
          <div className="form-group"><label className="form-label">Caliber</label><input className="form-control" value={form.caliber} onChange={e => set('caliber', e.target.value)} placeholder="9mm, .45 ACP..." /></div>
          <div className="form-group"><label className="form-label">Serial Number</label><input className="form-control" value={form.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
        </div>
      ) : (
        <div className="grid-2" style={{ gap: 16, marginBottom: 16 }}>
          <div className="form-group"><label className="form-label">Make / Brand</label><input className="form-control" value={form.make} onChange={e => set('make', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Model</label><input className="form-control" value={form.model} onChange={e => set('model', e.target.value)} /></div>
        </div>
      )}

      <div className="form-group" style={{ marginBottom: 20 }}><label className="form-label">Description</label><textarea className="form-control" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Additional details, included accessories, known issues..." /></div>

      {/* Images */}
      <div style={{ marginBottom: 24 }}>
        <label className="form-label">Photos</label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {images.map((img, i) => (
            <div key={i} style={{ position: 'relative', width: 80, height: 64 }}>
              <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: img.is_primary ? '2px solid #c9a227' : '2px solid #e8e8e8' }} />
              <button onClick={() => removeImage(img, i)} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#c62828', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>✕</button>
              {img.is_primary && <span style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 9, background: '#c9a227', color: '#fff', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>PRIMARY</span>}
            </div>
          ))}
          <label style={{ width: 80, height: 64, border: '2px dashed #e8e8e8', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 11, color: '#9a9a9a', fontWeight: 600, gap: 4 }}>
            {uploading ? '⏳' : '+'}<span>{uploading ? 'Uploading...' : 'Add Photo'}</span>
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={uploading} />
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : isNew ? 'Add Item' : 'Save Changes'}</button>
      </div>
    </div>
  );
}

export default function AdminInventory() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('available');
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  useEffect(() => { api.get('/categories').then(r => setCategories(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: LIMIT };
    if (search) params.search = search;
    if (status) params.status = status;
    api.get('/products/admin/all', { params }).then(r => { setProducts(r.data.products || []); setTotal(r.data.total || 0); }).catch(() => {}).finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Mark this item as deleted?')) return;
    await api.delete(`/products/${id}`);
    load();
  };

  if (showForm || editing) return <ProductForm product={editing} categories={categories} onSave={() => { setShowForm(false); setEditing(null); load(); }} onCancel={() => { setShowForm(false); setEditing(null); }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 2 }}>Inventory</h1>
          <p style={{ color: '#9a9a9a', fontSize: 13 }}>{total} items total</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Add Item</button>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <input className="form-control" style={{ maxWidth: 260 }} placeholder="Search title, SKU, make, model..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="form-control" style={{ width: 'auto' }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {STATUS_OPTS.filter(s => s !== 'deleted').map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        {loading ? <div className="loading"><div className="spinner" /></div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f7f2', borderBottom: '2px solid #e8e8e8' }}>
                {['Photo','Item','Category','Price','Condition','Channels','Status',''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#9a9a9a' }}>No items found</td></tr>
              ) : products.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid #f4f4f4' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '10px 16px', width: 56 }}>
                    {p.primary_image ? <img src={p.primary_image} alt="" style={{ width: 44, height: 36, objectFit: 'cover', borderRadius: 6 }} /> : <div style={{ width: 44, height: 36, background: '#f4f4f4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📦</div>}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1a2744', marginBottom: 2 }}>{p.title}</div>
                    <div style={{ fontSize: 11, color: '#9a9a9a' }}>{p.bravo_sku ? `SKU: ${p.bravo_sku}` : ''}{p.is_firearm ? ' 🔒 FFL' : ''}</div>
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: 12, color: '#666' }}>{p.subcategory_name || p.category_name}</td>
                  <td style={{ padding: '10px 16px', fontWeight: 700, color: '#1a2744', fontSize: 14 }}>${parseFloat(p.price).toFixed(2)}</td>
                  <td style={{ padding: '10px 16px', fontSize: 12, color: '#666', textTransform: 'capitalize' }}>{p.condition}</td>
                  <td style={{ padding: '10px 16px', fontSize: 11, color: '#9a9a9a' }}>
                    {!p.list_on_website && !p.list_on_ebay ? 'Bravo only' : [p.list_on_website && '🌐 Web', p.list_on_ebay && '🛒 eBay'].filter(Boolean).join(' · ')}
                  </td>
                  <td style={{ padding: '10px 16px' }}><span className={`badge ${p.status === 'available' ? 'badge-green' : p.status === 'sold' ? 'badge-red' : 'badge-gray'}`} style={{ fontSize: 10, textTransform: 'capitalize' }}>{p.status}</span></td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(p)}>Edit</button>
                      <button className="btn btn-sm" style={{ background: 'rgba(198,40,40,0.08)', color: '#c62828', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }} onClick={() => handleDelete(p.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {Math.ceil(total / LIMIT) > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn btn-outline btn-sm">← Prev</button>
          <span style={{ padding: '6px 14px', fontSize: 13, color: '#666' }}>Page {page} of {Math.ceil(total / LIMIT)}</span>
          <button disabled={page === Math.ceil(total / LIMIT)} onClick={() => setPage(p => p + 1)} className="btn btn-outline btn-sm">Next →</button>
        </div>
      )}
    </div>
  );
}
